import React from "react";
import {
    Paper,
    TextField,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Box,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableSortLabel,
    IconButton,
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import useAmenities from "../../hooks/useAmenities";
import MaterialIconPicker from "../../utils/MaterialIconPicker";
import { MaterialIcon } from "../../utils/materialIcons";

// ---- Sort utils ----
function getCellValue(row, key) {
    switch (key) {
        case "id":
            return row.id ?? 0;
        case "name":
            return row.name ?? "";
        case "label_de":
            return row.label_de ?? "";
        case "label_en":
            return row.label_en ?? "";
        case "label_fr":
            return row.label_fr ?? "";
        case "label_it":
            return row.label_it ?? "";
        default:
            return "";
    }
}

function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);

    const na = typeof va === "number" ? va : Number(va);
    const nb = typeof vb === "number" ? vb : Number(vb);

    if (Number.isFinite(na) && Number.isFinite(nb)) {
        return nb - na;
    }

    return (vb ?? "").toString().localeCompare((va ?? "").toString(), "de", {
        sensitivity: "base",
    });
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    return [...array]
        .map((el, idx) => [el, idx])
        .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
        .map((el) => el[0]);
}

function cleanOpt(v) {
    const s = (v || "").trim();
    return s ? s : null;
}

export default function AmenitiesPage() {

    const {
        amenities,
        loading,
        error,
        fetchAmenities,
        createAmenity,
        updateAmenity,
        deleteAmenity,
    } = useAmenities();

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("id");

    const [search, setSearch] = React.useState("");

    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState({
        name: "",
        label_de: "",
        label_en: "",
        label_fr: "",
        label_it: "",
        icon_name: "",
    });

    const [editingAmenity, setEditingAmenity] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    const filteredAmenities = React.useMemo(() => {
        const q = search.toLowerCase();

        return (amenities || []).filter((a) =>
            (a.name || "").toLowerCase().includes(q) ||
            (a.label_de || "").toLowerCase().includes(q) ||
            (a.label_en || "").toLowerCase().includes(q) ||
            (a.label_fr || "").toLowerCase().includes(q) ||
            (a.label_it || "").toLowerCase().includes(q)
        );
    }, [amenities, search]);

    const rows = React.useMemo(
        () => stableSort(filteredAmenities, getComparator(order, orderBy)),
        [filteredAmenities, order, orderBy]
    );

    const columns = [
        { id: "id", label: "ID" },
        { id: "label_de", label: "Label (DE)" },
    ];

    const handleOpenCreate = () => {
        setEditingAmenity(null);
        setForm({
            name: "",
            label_de: "",
            label_en: "",
            label_fr: "",
            label_it: "",
            icon_name: "",
        });
        setSubmitError("");
        setOpen(true);
    };

    const handleOpenEdit = (a) => {
        setEditingAmenity(a);
        setForm({
            name: a.name || "",
            label_de: a.label_de || "",
            label_en: a.label_en || "",
            label_fr: a.label_fr || "",
            label_it: a.label_it || "",
            icon_name: a.icon_name || "",
        });
        setSubmitError("");
        setOpen(true);
    };

    const handleSave = async () => {

        setSubmitError("");

        const name = (form.name || "").trim();
        const label_de = (form.label_de || "").trim();

        if (!name) {
            setSubmitError("Key/Name ist Pflichtfeld.");
            return;
        }

        if (!label_de) {
            setSubmitError("Label (DE) ist Pflichtfeld.");
            return;
        }

        const payload = {
            name,
            label_de,
            label_en: cleanOpt(form.label_en),
            label_fr: cleanOpt(form.label_fr),
            label_it: cleanOpt(form.label_it),
            icon_name: cleanOpt(form.icon_name),
        };

        setSubmitting(true);

        try {

            if (editingAmenity) {
                await updateAmenity(editingAmenity.id, payload);
            } else {
                await createAmenity(payload);
            }

            setOpen(false);

        } catch (e) {

            setSubmitError(e.message || "Speichern fehlgeschlagen.");

        } finally {

            setSubmitting(false);

        }
    };

    return (
        <PageContainer
            title="Ausstattung"
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchAmenities} disabled={loading}>
                        Aktualisieren
                    </Button>
                    <Button variant="contained" onClick={handleOpenCreate}>
                        Neue Ausstattung
                    </Button>
                </Box>
            }
        >

            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Ausstattung suchen"
                    placeholder="Name oder Label eingeben..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: "70vh",
                    overflow: "auto"
                }}
            >

                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (

                    <Table>

                        <TableHead>
                            <TableRow>

                                {columns.map((c) => (
                                    <TableCell key={c.id}>
                                        <TableSortLabel
                                            active={orderBy === c.id}
                                            direction={order}
                                            onClick={() => {
                                                const isAsc = orderBy === c.id && order === "asc";
                                                setOrder(isAsc ? "desc" : "asc");
                                                setOrderBy(c.id);
                                            }}
                                        >
                                            {c.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}

                                <TableCell align="right">Aktionen</TableCell>

                            </TableRow>
                        </TableHead>

                        <TableBody>

                            {rows.map((a) => (
                                <TableRow key={a.id} hover>

                                    <TableCell>{a.id}</TableCell>

                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <MaterialIcon iconName={a.icon_name} />
                                            {a.label_de || "-"}
                                        </Box>
                                    </TableCell>

                                    <TableCell align="right">

                                        <IconButton onClick={() => handleOpenEdit(a)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>

                                        <IconButton
                                            color="error"
                                            onClick={() => {
                                                setDeleteTarget(a);
                                                setDeleteDialogOpen(true);
                                                setDeleteError("");
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>

                                    </TableCell>

                                </TableRow>
                            ))}

                        </TableBody>

                    </Table>
                )}

            </TableContainer>


            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="sm">

                <DialogTitle>
                    {editingAmenity ? "Ausstattung bearbeiten" : "Neue Ausstattung anlegen"}
                </DialogTitle>

                <DialogContent dividers>

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>

                        <TextField
                            fullWidth
                            label="Key/Name* (technisch)"
                            helperText="z.B. wifi, sauna, garage"
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        />

                        <TextField
                            fullWidth
                            label="Label (DE)*"
                            value={form.label_de}
                            onChange={(e) => setForm((f) => ({ ...f, label_de: e.target.value }))}
                        />

                        <TextField
                            fullWidth
                            label="Label (EN)"
                            value={form.label_en}
                            onChange={(e) => setForm((f) => ({ ...f, label_en: e.target.value }))}
                        />

                        <TextField
                            fullWidth
                            label="Label (FR)"
                            value={form.label_fr}
                            onChange={(e) => setForm((f) => ({ ...f, label_fr: e.target.value }))}
                        />

                        <TextField
                            fullWidth
                            label="Label (IT)"
                            value={form.label_it}
                            onChange={(e) => setForm((f) => ({ ...f, label_it: e.target.value }))}
                        />

                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <MaterialIconPicker label="Material Icon" value={form.icon_name}
                            onChange={(icon_name) => setForm((f) => ({ ...f, icon_name }))} />
                    </Box>

                    {submitError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {submitError}
                        </Alert>
                    )}

                </DialogContent>

                <DialogActions>

                    <Button onClick={() => setOpen(false)} disabled={submitting}>
                        Abbrechen
                    </Button>

                    <Button variant="contained" onClick={handleSave} disabled={submitting}>
                        {submitting ? "Speichern…" : "Speichern"}
                    </Button>

                </DialogActions>

            </Dialog>

        </PageContainer>
    );
}
